import React, { useState, useEffect } from "react"
import { Box } from "@mui/material"
import { CircularProgress, Box as MuiBox } from "@mui/material"
import CostHeader from "./CostHeader"
import EmptyState from "common/EmptyState"
import CostGrid from "./CostGrid"
import CostModal from "./CostModal"
import DeleteModal from "./DeleteModal"
import BlockedDeleteModal from "./DeleteModal/BlockedDeleteModal"
import {
  useOrganizationExpenses,
  useProjectsHeaders,
  useExpense,
} from "./hooks/useOrganizationExpenses"
import { useQueryClient } from "@tanstack/react-query"
import { useAuthContext } from "context/auth/authContext"
import { useLocation } from "react-router-dom"
import i18n from "i18n"

const GestionCostes = () => {
  const { user } = useAuthContext()
  if (!user) {
    return (
      <MuiBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <CircularProgress />
      </MuiBox>
    )
  }

  const organizationId = user["custom:organizationId"]
  const location = useLocation()
  const projectId = location.pathname.split("/")[2]

  const { data: fetchedCosts = [], isLoading } = useOrganizationExpenses(
    organizationId,
    projectId,
    !!organizationId,
  )
  const { data: fetchedProjectsHeaders = [] } =
    useProjectsHeaders(organizationId)

  const filteredProjectsHeaders = projectId
    ? fetchedProjectsHeaders.filter(p => p.projectId === projectId)
    : fetchedProjectsHeaders

  const queryClient = useQueryClient()
  const [selectedExpenseId, setSelectedExpenseId] = useState(null)
  const { data: selectedExpense } = useExpense(
    organizationId,
    selectedExpenseId,
    !!selectedExpenseId,
  )
  const [openModal, setOpenModal] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [editingCost, setEditingCost] = useState(null)
  const [deletingCost, setDeletingCost] = useState(null)

  const hasLinkedPurchaseOrder = cost => {
    const purchaseOrderId = cost?.purchaseOrderId
    return purchaseOrderId != null && String(purchaseOrderId).trim() !== ""
  }

  const handleAddNewCost = () => {
    setEditingCost(null)
    setOpenModal(true)
  }

  const handleEditCost = cost => {
    if (cost?.expenseId) {
      queryClient.invalidateQueries({
        queryKey: ["single-expense", organizationId, cost.expenseId],
      })
    }

    setSelectedExpenseId(cost?.expenseId || null)
  }

  useEffect(() => {
    if (selectedExpense) {
      setEditingCost(selectedExpense)
      setOpenModal(true)
    }
  }, [selectedExpense])

  const handleDeleteCost = cost => {
    setDeletingCost(cost)
    setOpenDeleteModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setEditingCost(null)
    setSelectedExpenseId(null)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
    setDeletingCost(null)
  }

  const handleSaveCost = updated => {
    if (!updated?.expenseId) return

    setEditingCost(prev =>
      prev && prev.expenseId === updated.expenseId
        ? { ...prev, ...updated }
        : prev,
    )

    queryClient.setQueryData(
      ["organization-expenses", organizationId, projectId],
      old =>
        Array.isArray(old)
          ? old.map(c =>
              c.expenseId === updated.expenseId ? { ...c, ...updated } : c,
            )
          : old,
    )

    queryClient.setQueryData(
      ["single-expense", organizationId, updated.expenseId],
      old => ({ ...(old || {}), ...updated }),
    )

    queryClient.invalidateQueries({
      queryKey: ["organization-expenses", organizationId, projectId],
      refetchType: "inactive",
    })
    queryClient.invalidateQueries({
      queryKey: ["single-expense", organizationId, updated.expenseId],
    })
  }

  return (
    <Box>
      <CostHeader onAddNewCost={handleAddNewCost} />
      {isLoading ? (
        <MuiBox
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <CircularProgress />
        </MuiBox>
      ) : fetchedCosts.length > 0 ? (
        <CostGrid
          costs={fetchedCosts}
          organizationId={organizationId}
          onEditCost={handleEditCost}
          onDeleteCost={handleDeleteCost}
        />
      ) : (
        <EmptyState
          title={i18n.t("costs.noCostsYet")}
          buttonText={i18n.t("costs.newCost")}
          onButtonClick={handleAddNewCost}
        />
      )}

      <CostModal
        open={openModal}
        onClose={handleCloseModal}
        onSave={handleSaveCost}
        costData={editingCost}
        projectId={projectId}
        projectsHeaders={filteredProjectsHeaders}
      />

      {openDeleteModal &&
        (hasLinkedPurchaseOrder(deletingCost) ? (
          <BlockedDeleteModal
            open={openDeleteModal}
            onClose={handleCloseDeleteModal}
          />
        ) : (
          <DeleteModal
            open={openDeleteModal}
            onClose={handleCloseDeleteModal}
            costData={deletingCost}
          />
        ))}
    </Box>
  )
}

export default GestionCostes
